import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Send, 
  Wallet, 
  ArrowRight, 
  AlertCircle, 
  CheckCircle, 
  Shield, 
  Zap, 
  LogOut, 
  LogIn,
  Loader2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Clock // ← Ajoutez cette importation
} from 'lucide-react';
import { 
  checkBalance,
  transferIntraMethod,
  getMerchantBalances,
  requiresMakerChecker
} from '../../services/transferService';

interface WalletToWalletForm {
  method: string;
  currency: string;
  amount: string;
  from_action: 'deposit' | 'withdrawal';
  to_action: 'deposit' | 'withdrawal';
  note: string;
}

interface BalanceInfo {
  depositBalance: number;
  withdrawalBalance: number;
  availableForDeposit: number;
  availableForWithdrawal: number;
}

const WalletToWallet: React.FC = () => {
  const { countryConfig } = useLocalization();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [processingMessage, setProcessingMessage] = useState<string>('');
  const [balances, setBalances] = useState<BalanceInfo>({
    depositBalance: 0,
    withdrawalBalance: 0,
    availableForDeposit: 0,
    availableForWithdrawal: 0
  });
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [transferResult, setTransferResult] = useState<any>(null);

  const getOperatorsForCountry = () => {
    const operatorColors: { [key: string]: string } = {
      'mpesa': '#E60000',
      'orange': '#FF8C00',
      'airtel': '#E53E3E',
      'afrimoney': '#6B46C1',
    };

    return ['mpesa', 'orange', 'airtel', 'afrimoney'].map(operator => ({
      id: operator,
      name: operator === 'mpesa' ? 'M-Pesa' : operator.charAt(0).toUpperCase() + operator.slice(1),
      color: operatorColors[operator] || '#6B46C1'
    }));
  };

  const operators = getOperatorsForCountry();
  const currencies = countryConfig.currencies;

  const [formData, setFormData] = useState<WalletToWalletForm>({
    method: operators[0]?.id || 'mpesa',
    currency: countryConfig.primaryCurrency,
    amount: '',
    from_action: 'deposit',
    to_action: 'withdrawal',
    note: 'Intra-operator wallet transfer'
  });

  // Fonction pour charger les soldes - CORRIGÉE
  const loadBalances = useCallback(async (showLoader = true) => {
    if (!user?.merchant_code) return;
    
    try {
      if (showLoader) {
        setLoadingBalances(true);
      }
      
      const balanceResponse = await checkBalance(
        formData.method,
        formData.currency
      );
      
      // CORRECTION : Calculer correctement les soldes disponibles
      const depositBalance = parseFloat(balanceResponse.realtime_deposit);
      const withdrawalBalance = parseFloat(balanceResponse.realtime_withdrawal);
      const availableWithdrawal = parseFloat(balanceResponse.available_for_withdrawal);
      
      setBalances({
        depositBalance: depositBalance,
        withdrawalBalance: withdrawalBalance,
        availableForDeposit: depositBalance, // Disponible pour dépôt = solde dépôt
        availableForWithdrawal: availableWithdrawal // Disponible pour retrait = solde retrait - réservé
      });
      
    } catch (error) {
      console.error('Error loading balances:', error);
      
      // Fallback à l'ancienne API
      try {
        const data = await getMerchantBalances(
          user.merchant_code,
          formData.currency,
          formData.method.toLowerCase() === 'mpesa' ? 'mpesa' : formData.method
        );
        
        const depositBalance = data.deposit_balance || 0;
        const withdrawalBalance = data.withdrawal_balance || 0;
        
        setBalances({
          depositBalance: depositBalance,
          withdrawalBalance: withdrawalBalance,
          availableForDeposit: depositBalance, // Même correction pour le fallback
          availableForWithdrawal: withdrawalBalance // Note: fallback n'a pas reserved_withdrawal
        });
      } catch (fallbackError) {
        setMessage({ 
          type: 'error', 
          text: 'Error loading account balances' 
        });
      }
    } finally {
      if (showLoader) {
        setLoadingBalances(false);
      }
    }
  }, [user?.merchant_code, formData.method, formData.currency]);

  // Load balances when method or currency changes
  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setMessage(null);
      setProcessingMessage('Validating transfer details...');
      setShowSuccess(false);
      setTransferResult(null);

      try {
        const amount = parseFloat(formData.amount);
        
        if (amount <= 0) {
          setMessage({ type: 'error', text: 'Amount must be greater than 0' });
          setLoading(false);
          setProcessingMessage('');
          return;
        }

        // Vérifier le solde disponible
        const sourceBalance = formData.from_action === 'deposit' 
          ? balances.depositBalance 
          : balances.withdrawalBalance;
        
        const availableBalance = formData.from_action === 'deposit'
          ? balances.availableForDeposit
          : balances.availableForWithdrawal;

        setProcessingMessage('Checking account balance...');
        
        if (amount > availableBalance) {
          setMessage({ 
            type: 'error', 
            text: `Insufficient balance. Available: ${availableBalance.toFixed(2)} ${formData.currency}` 
          });
          setLoading(false);
          setProcessingMessage('');
          return;
        }

        // Préparer les données de transfert
        const transferData = {
          method: formData.method,
          currency: formData.currency,
          amount: amount,
          from_action: formData.from_action,
          to_action: formData.to_action,
          note: formData.note
        };

        setProcessingMessage('Processing transfer...');
        
        const result = await transferIntraMethod(transferData);
        
        console.log('Transfer result:', result);

        // Mettre à jour les soldes localement
        await loadBalances(false);
        
        setTransferResult(result);
        setShowSuccess(true);
        
        // Afficher le message approprié selon le statut
        if (result.status === 'PENDING') {
          setMessage({ 
            type: 'info', 
            text: result.message || 'Transfer pending approval from backoffice. You will be notified once processed.' 
          });
        } else {
          setMessage({ 
            type: 'success', 
            text: result.message || `Transfer completed successfully! Reference: ${result.reference}` 
          });
        }
        
        // Reset form
        setFormData(prev => ({
          ...prev,
          amount: '',
          note: 'Intra-operator wallet transfer'
        }));
        
      } catch (error: any) {
        console.error('Transfer error:', error);
        
        let errorMessage = 'Error processing transfer';
        
        if (error.response?.data) {
          if (error.response.data.detail) {
            if (typeof error.response.data.detail === 'object') {
              errorMessage = error.response.data.detail.message || JSON.stringify(error.response.data.detail);
            } else {
              errorMessage = error.response.data.detail;
            }
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setMessage({ 
          type: 'error', 
          text: errorMessage 
        });
      } finally {
        setLoading(false);
        setProcessingMessage('');
      }
    };

  const selectedOperator = operators.find(op => op.id === formData.method);
  const transferAmount = parseFloat(formData.amount || '0');
  const needsApproval = false;

  const getWalletLabel = (action: 'deposit' | 'withdrawal') => {
    return action === 'deposit' ? 'Pay-in Wallet' : 'Pay-out Wallet';
  };

  const getWalletIcon = (action: 'deposit' | 'withdrawal') => {
    return action === 'deposit' ? <LogIn className="h-4 w-4" /> : <LogOut className="h-4 w-4" />;
  };

  const getWalletColor = (action: 'deposit' | 'withdrawal') => {
    return action === 'deposit' ? 'text-blue-600' : 'text-green-600';
  };

  const getWalletBgColor = (action: 'deposit' | 'withdrawal') => {
    return action === 'deposit' ? 'bg-blue-100 border-blue-200' : 'bg-green-100 border-green-200';
  };

  return (
    <div className="max-w-3xl mx-auto p-4 relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm"></div>
          
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-100">
            <div className="text-center">
              <div className="relative mx-auto mb-6">
                <div className="w-20 h-20 border-4 border-gray-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-20 h-20 border-4 border-[#63d400] border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <Zap className="h-8 w-8 text-[#63d400] animate-pulse" />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Processing Transfer
              </h3>
              <p className="text-gray-600 mb-6">
                {processingMessage}
              </p>
              
              <div className="space-y-3 text-left max-w-xs mx-auto">
                <div className={`flex items-center ${processingMessage.includes('Validating') ? 'text-[#63d400]' : 'text-gray-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${processingMessage.includes('Validating') ? 'bg-[#63d400] text-white' : 'bg-gray-100'}`}>
                    {processingMessage.includes('Validating') ? '✓' : '1'}
                  </div>
                  <span className="text-sm font-medium">Validating details</span>
                </div>
                
                <div className={`flex items-center ${processingMessage.includes('Checking') ? 'text-[#63d400]' : 'text-gray-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${processingMessage.includes('Checking') ? 'bg-[#63d400] text-white' : 'bg-gray-100'}`}>
                    {processingMessage.includes('Checking') ? '✓' : '2'}
                  </div>
                  <span className="text-sm font-medium">Checking balance</span>
                </div>
                
                <div className={`flex items-center ${processingMessage.includes('Processing') ? 'text-[#63d400]' : 'text-gray-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${processingMessage.includes('Processing') ? 'bg-[#63d400] text-white' : 'bg-gray-100'}`}>
                    {processingMessage.includes('Processing') ? '✓' : '3'}
                  </div>
                  <span className="text-sm font-medium">Processing transfer</span>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Please wait while we process your transfer. Do not close this window.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success/Pending Modal */}
      {showSuccess && transferResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm"></div>
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-100">
            <div className="text-center">
              {/* Icône dynamique selon le statut */}
              <div className={`w-20 h-20 ${
                transferResult.status === 'PENDING' ? 'bg-yellow-100' : 'bg-green-100'
              } rounded-full flex items-center justify-center mx-auto mb-6`}>
                {transferResult.status === 'PENDING' ? (
                  <Clock className="h-10 w-10 text-yellow-600" />
                ) : (
                  <CheckCircle className="h-10 w-10 text-green-600" />
                )}
              </div>
              
              {/* Titre dynamique */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {transferResult.status === 'PENDING' ? 'Transfer Submitted!' : 'Transfer Successful!'}
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-left space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-mono text-sm font-semibold">{transferResult.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-bold text-gray-900">
                      {transferResult.amount} {transferResult.currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="font-medium">
                      {getWalletLabel(formData.from_action)} ({transferResult.from_wallet.method})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">To:</span>
                    <span className="font-medium">
                      {getWalletLabel(formData.to_action)} ({transferResult.to_wallet.method})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      transferResult.status === 'PENDING' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {transferResult.status === 'PENDING' ? 'Pending Approval' : 'Completed'}
                    </span>
                  </div>
                  
                  {/* Message supplémentaire pour les transferts en attente */}
                  {transferResult.status === 'PENDING' && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-yellow-600 text-center">
                        ⏳ {transferResult.message || 'Transfer pending approval from backoffice. You will be notified once processed.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => {
                  setShowSuccess(false);
                  loadBalances(false);
                }}
                className={`w-full ${
                  transferResult.status === 'PENDING'
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                    : 'bg-gradient-to-r from-[#63d400] to-[#7ee000]'
                } text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-200`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-gradient-to-r from-[#63d400] to-[#7ee000] rounded-xl p-6 mb-6 text-white">
        <div className="flex items-center">
          <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold mb-1">Intra-Operator Transfer</h1>
            <p className="text-white text-opacity-90 text-sm">
              Move funds between wallets within the same operator
            </p>
          </div>
        </div>
      </div>

      {/* Alert Message */}
      {message && (
        <div className={`p-3 rounded-lg mb-4 flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : message.type === 'info'
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          ) : message.type === 'info' ? (
            <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Pay-in Wallet */}
        <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Pay-in Wallet</h3>
              <p className="text-xs text-gray-500">Deposit balance</p>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center border border-blue-200">
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          {loadingBalances ? (
            <div className="h-10 flex items-center">
              <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ) : (
            <div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {balances.depositBalance.toLocaleString()} {formData.currency}
              </p>
              <p className="text-xs text-green-600 font-medium">
                Available: {balances.availableForDeposit.toLocaleString()} {formData.currency}
              </p>
            </div>
          )}
          <div className="flex items-center text-gray-600 mt-2">
            <div 
              className="w-2 h-2 rounded-full mr-2"
              style={{ backgroundColor: selectedOperator?.color }}
            ></div>
            <span className="text-xs">{selectedOperator?.name}</span>
          </div>
        </div>
        
        {/* Pay-out Wallet */}
        <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Pay-out Wallet</h3>
              <p className="text-xs text-gray-500">Withdrawal balance</p>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center border border-green-200">
              <TrendingDown className="h-4 w-4 text-green-600" />
            </div>
          </div>
          {loadingBalances ? (
            <div className="h-10 flex items-center">
              <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ) : (
            <div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {balances.withdrawalBalance.toLocaleString()} {formData.currency}
              </p>
              <p className="text-xs text-green-600 font-medium">
                Available: {balances.availableForWithdrawal.toLocaleString()} {formData.currency}
              </p>
            </div>
          )}
          <div className="flex items-center text-gray-600 mt-2">
            <div 
              className="w-2 h-2 rounded-full mr-2"
              style={{ backgroundColor: selectedOperator?.color }}
            ></div>
            <span className="text-xs">{selectedOperator?.name}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Transfer Configuration */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Wallet className="h-4 w-4 mr-2 text-[#63d400]" />
            Transfer Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Operator
              </label>
              <select
                value={formData.method}
                onChange={(e) => setFormData({...formData, method: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
                required
                disabled={loading}
              >
                {operators.map(operator => (
                  <option key={operator.id} value={operator.id}>
                    {operator.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Wallet
              </label>
              <select
                value={formData.from_action}
                onChange={(e) => setFormData({...formData, from_action: e.target.value as 'deposit' | 'withdrawal'})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
                required
                disabled={loading}
              >
                <option value="deposit">Pay-in Wallet</option>
                <option value="withdrawal">Pay-out Wallet</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Wallet
              </label>
              <select
                value={formData.to_action}
                onChange={(e) => setFormData({...formData, to_action: e.target.value as 'deposit' | 'withdrawal'})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
                required
                disabled={loading}
              >
                <option value="withdrawal">Pay-out Wallet</option>
                <option value="deposit">Pay-in Wallet</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({...formData, currency: e.target.value})}
              className="w-full md:w-1/4 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
              required
              disabled={loading}
            >
              {currencies.map(currency => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Transfer Details */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Send className="h-4 w-4 mr-2 text-[#63d400]" />
            Transfer Details
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer Amount
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Available in {getWalletLabel(formData.from_action)}: {
                  formData.from_action === 'deposit' 
                    ? balances.availableForDeposit.toLocaleString() 
                    : balances.availableForWithdrawal.toLocaleString()
                } {formData.currency}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (Optional)
              </label>
              <input
                type="text"
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Transfer purpose or notes"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Transfer Summary */}
        {transferAmount > 0 && !loading && (
          <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
            <h3 className="text-lg font-semibold text-[#000065] mb-4">Transfer Summary</h3>
            
            <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg border border-blue-100">
              <div className={`flex items-center p-3 rounded-lg ${getWalletBgColor(formData.from_action)} flex-1`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${getWalletColor(formData.from_action)}`}>
                  {getWalletIcon(formData.from_action)}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{getWalletLabel(formData.from_action)}</p>
                  <p className="text-xs text-gray-600">{selectedOperator?.name}</p>
                  <p className="text-xs text-red-600 font-medium">
                    - {transferAmount.toLocaleString()} {formData.currency}
                  </p>
                </div>
              </div>
              
              <ArrowRight className="h-5 w-5 text-[#63d400] mx-3 flex-shrink-0" />
              
              <div className={`flex items-center p-3 rounded-lg ${getWalletBgColor(formData.to_action)} flex-1`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${getWalletColor(formData.to_action)}`}>
                  {getWalletIcon(formData.to_action)}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{getWalletLabel(formData.to_action)}</p>
                  <p className="text-xs text-gray-600">{selectedOperator?.name}</p>
                  <p className="text-xs text-green-600 font-medium">
                    + {transferAmount.toLocaleString()} {formData.currency}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm bg-white rounded-lg p-4 border border-blue-100">
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Transfer Amount:</span>
                <span className="font-semibold text-gray-900">{formData.amount} {formData.currency}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Transfer Type:</span>
                <span className="font-medium text-blue-600">
                  {formData.from_action} → {formData.to_action}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="font-medium text-[#000065]">Status:</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  Ready to Process
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <button
            type="submit"
            disabled={loading || !formData.amount || parseFloat(formData.amount) <= 0}
            className="w-full bg-gradient-to-r from-[#63d400] to-[#7ee000] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Processing Transfer...
              </div>
            ) : (
              'Execute Transfer Now'
            )}
          </button>

          <div className="text-center mt-3">
            <p className="text-xs text-gray-500 flex items-center justify-center">
              <Shield className="h-3 w-3 mr-1" />
              Instant transfer • No additional verification required
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WalletToWallet;